import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        this.#ensureAgeBeforeColor(modalContent);
      }
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /**
   * Applies page-specific styling hooks to the quick-add dialog.
   * @param {QuickAddDialog} dialogComponent
   */
  #updateModalVariant(dialogComponent) {
    const isCollectionAllPage = window.location.pathname.includes('/collections/all');
    dialogComponent.refs.dialog.classList.toggle('quick-add-modal--collection-all', isCollectionAllPage);
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#updateModalVariant(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
    dialog.addEventListener(
      'animationend',
      () => {
        // Match the stable state normally reached after a variant switch.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#stabilizeDialogMediaLayout();
          });
        });
      },
      { once: true }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Shopify's accelerated checkout component can fail inside the quick-add modal
    // when it receives an invalid storefront token from the fetched product markup.
    // Remove it here so the modal still works without the broken native button.
    productGrid.querySelector('.accelerated-checkout-block')?.remove();

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#ensureRatingBlock(modalContent);
    this.#stripCurrencyCode(modalContent);
    this.#ensureAgeBeforeColor(modalContent);
    this.#syncVariantSelection(modalContent);
    this.#ensureQuickAddBuyNowButton(modalContent);
  }

  #stabilizeDialogMediaLayout() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    // Force a non-animated reselect so slideshow re-measures after dialog becomes visible.
    const slideshows = Array.from(modalContent.querySelectorAll('slideshow-component'));
    for (const element of slideshows) {
      if (!(element instanceof HTMLElement)) continue;
      const slideshow = /** @type {any} */ (element);
      if (typeof slideshow.select !== 'function') continue;

      const currentSlide = element.querySelector('slideshow-slide[aria-hidden="false"]') || element.querySelector('slideshow-slide');
      const slideId = currentSlide?.getAttribute('slide-id');

      if (slideId) {
        slideshow.select({ id: slideId }, undefined, { animate: false });
      } else {
        slideshow.select(0, undefined, { animate: false });
      }
    }
  }

  /**
   * Ensures the quick-add modal shows a rating block, even when the product has no review content.
   * @param {Element} modalContent - The modal content element
   */
  #ensureRatingBlock(modalContent) {
    if (modalContent.querySelector('.rating-wrapper')) return;

    const ratingWrapper = document.createElement('div');
    ratingWrapper.className = 'rating-wrapper rating-color--primary justify-left size-style rating--empty';
    ratingWrapper.style.setProperty('--size-style-width', '100%');

    ratingWrapper.innerHTML = `
      <svg class="visually-hidden" style="width: 0; height: 0;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="presentation">
        <defs>
          <linearGradient id="half" x1="0" x2="100%" y1="0" y2="0">
            <stop offset="50%" stop-color="var(--star-fill-color)"></stop>
            <stop offset="50%" stop-color="rgb(var(--star-fill-color-rgb) / var(--opacity-20))"></stop>
          </linearGradient>

          <symbol xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" id="star">
            <path d="M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z"></path>
          </symbol>
        </defs>
      </svg>
      <div class="rating" style="--star-size: calc(var(--font-paragraph--size) * 1.1);" aria-label="Rating of this product is 0 out of 5">
        <svg class="stars" viewBox="0 0 32 32" style="--empty-star-fill-color: rgb(var(--star-fill-color-rgb) / var(--opacity-20))" role="presentation"><use xlink:href="#star"></use></svg>
        <svg class="stars" viewBox="0 0 32 32" style="--empty-star-fill-color: rgb(var(--star-fill-color-rgb) / var(--opacity-20))" role="presentation"><use xlink:href="#star"></use></svg>
        <svg class="stars" viewBox="0 0 32 32" style="--empty-star-fill-color: rgb(var(--star-fill-color-rgb) / var(--opacity-20))" role="presentation"><use xlink:href="#star"></use></svg>
        <svg class="stars" viewBox="0 0 32 32" style="--empty-star-fill-color: rgb(var(--star-fill-color-rgb) / var(--opacity-20))" role="presentation"><use xlink:href="#star"></use></svg>
        <svg class="stars" viewBox="0 0 32 32" style="--empty-star-fill-color: rgb(var(--star-fill-color-rgb) / var(--opacity-20))" role="presentation"><use xlink:href="#star"></use></svg>
      </div>
      <p class="rating-count paragraph">(0)</p>
    `;

    const mobileProductHeader = modalContent.querySelector('.product-header');
    const mobilePrice = modalContent.querySelector('.product-header product-price');
    if (mobileProductHeader && mobilePrice) {
      mobileProductHeader.insertBefore(ratingWrapper, mobilePrice);
      return;
    }

    const desktopViewTitle = modalContent.querySelector('.view-product-title');
    if (desktopViewTitle) {
      desktopViewTitle.insertAdjacentElement('afterend', ratingWrapper);
      return;
    }

    const productDetailsContent = modalContent.querySelector('.product-details .group-block-content');
    if (productDetailsContent) {
      productDetailsContent.insertAdjacentElement('afterbegin', ratingWrapper);
      return;
    }

    modalContent.prepend(ratingWrapper);
  }

  /**
   * Removes currency codes from the quick-add modal price display.
   * @param {Element} modalContent - The modal content element
   */
  #stripCurrencyCode(modalContent) {
    const priceElements = modalContent.querySelectorAll('.product-details .price, .product-details .compare-at-price, .product-details .unit-price');

    for (const element of priceElements) {
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      let textNode = walker.nextNode();

      while (textNode) {
        const originalText = textNode.nodeValue ?? '';
        const cleanedText = originalText.replace(/\b[A-Z]{3}\b/g, '').replace(/\s{2,}/g, ' ');

        if (cleanedText !== originalText) {
          textNode.nodeValue = cleanedText;
        }

        textNode = walker.nextNode();
      }
    }
  }

  /**
   * Keeps variant option order stable: Age first, then Color.
   * @param {Element} modalContent - The modal content element
   */
  #ensureAgeBeforeColor(modalContent) {
    const variantForm = modalContent.querySelector('.product-details variant-picker .variant-picker__form');
    if (!variantForm) return;

    const optionBlocks = Array.from(variantForm.children).filter(
      (child) =>
        child instanceof HTMLElement &&
        (child.matches('.variant-option--buttons') || child.matches('.variant-option[data-testid="variant-option-single"]'))
    );

    /** @param {string} value */
    const normalizeOptionName = (value) => value.trim().toLowerCase().replace(/[:：]/g, '');

    /** @param {Element} element */
    const getOptionName = (element) => {
      if (!(element instanceof HTMLElement)) return '';
      if (element.classList.contains('variant-option--color')) return 'color';
      if (element.classList.contains('variant-option--age')) return 'age';

      const legendText = element.querySelector('legend')?.textContent;
      if (legendText) {
        const normalizedLegend = normalizeOptionName(legendText);
        if (normalizedLegend.includes('color') || normalizedLegend.includes('colour') || normalizedLegend.includes('颜色')) {
          return 'color';
        }
        if (normalizedLegend.includes('age') || normalizedLegend.includes('size') || normalizedLegend.includes('年龄')) {
          return 'age';
        }
      }

      if (element.dataset.testid === 'variant-option-single') {
        for (const node of element.childNodes) {
          if (node.nodeType !== Node.TEXT_NODE) continue;
          const text = node.textContent?.trim();
          if (!text) continue;
          const normalizedText = normalizeOptionName(text);
          if (normalizedText.includes('color') || normalizedText.includes('colour') || normalizedText.includes('颜色')) return 'color';
          if (normalizedText.includes('age') || normalizedText.includes('size') || normalizedText.includes('年龄')) return 'age';
        }
      }

      return '';
    };

    const colorOption = optionBlocks.find((block) => getOptionName(block) === 'color');
    const ageOption = optionBlocks.find((block) => getOptionName(block) === 'age');

    if (!colorOption || !ageOption || colorOption === ageOption) return;

    const ageComesAfterColor = Boolean(ageOption.compareDocumentPosition(colorOption) & Node.DOCUMENT_POSITION_PRECEDING);
    if (!ageComesAfterColor) return;

    // Keep option order stable: Age first, then Color.
    variantForm.insertBefore(ageOption, colorOption);
  }


  /**
   * Inserts a theme-controlled Buy it now button into the quick-add modal.
   * @param {Element} modalContent - The modal content element
   */
  #ensureQuickAddBuyNowButton(modalContent) {
    if (!modalContent.closest('.quick-add-modal')) return;

    const productFormButtons = modalContent.querySelector('.product-form-buttons');
    const addToCartButton = modalContent.querySelector('.add-to-cart-button');
    if (!productFormButtons || !addToCartButton) return;

    let buyNowButton = /** @type {HTMLButtonElement | null} */ (
      productFormButtons.querySelector('.quick-add-buy-now-button')
    );

    if (!buyNowButton) {
      buyNowButton = document.createElement('button');
      buyNowButton.type = 'submit';
      buyNowButton.name = 'checkout';
      buyNowButton.className = 'button quick-add-buy-now-button';
      buyNowButton.textContent = 'Buy it now';
      buyNowButton.setAttribute('aria-label', 'Buy it now');
      buyNowButton.dataset.buyNow = 'true';
      productFormButtons.appendChild(buyNowButton);
    }

    buyNowButton.disabled = addToCartButton instanceof HTMLButtonElement ? addToCartButton.disabled : false;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

    this.#enforceAgeBeforeColorOrder();
  };

  #enforceAgeBeforeColorOrder() {
    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    const variantForm = modalContent.querySelector('.product-details variant-picker .variant-picker__form');
    if (!(variantForm instanceof HTMLElement)) return;

    const colorOption = variantForm.querySelector('.variant-option--color');
    const ageOption = variantForm.querySelector('.variant-option--age');
    if (!(colorOption instanceof HTMLElement) || !(ageOption instanceof HTMLElement)) return;

    const ageComesAfterColor = Boolean(ageOption.compareDocumentPosition(colorOption) & Node.DOCUMENT_POSITION_PRECEDING);
    if (!ageComesAfterColor) return;

    variantForm.insertBefore(ageOption, colorOption);
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
